import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template54: Página de Harajuku (Santuario Meiji) de Tokio
// Copia DIRECTA del componente Page10.jsx de guia-tokio, adaptado para usar las props del sistema guia
const Template54 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [meijiError, setMeijiError] = useState(false);
	const [meiji2Error, setMeiji2Error] = useState(false);
	const [santuarioError, setSantuarioError] = useState(false);
	const [santuario2Error, setSantuario2Error] = useState(false);
	const [bodaError, setBodaError] = useState(false);
	const [logoError, setLogoError] = useState(false);

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const meijiImage = getImageSrc("meiji");
	const meiji2Image = getImageSrc("meiji2");
	const santuarioImage = getImageSrc("santuario");
	const santuario2Image = getImageSrc("santuario2");
	const bodaImage = getImageSrc("boda");

	const introText = page.texts?.[1]?.content;
	const meijiTitle = page.texts?.[2]?.content;
	const meijiSubtitle = page.texts?.[14]?.content;

	// Parsear Sabías que (varios <p> con emoji + texto)
	const parseSabiasQue = (htmlText) => {
		if (!htmlText) return [];
		const parser = new DOMParser();
		const doc = parser.parseFromString(htmlText, "text/html");
		const paragraphs = doc.querySelectorAll("p");
		const items = [];
		paragraphs.forEach((p) => {
			const text = p.textContent ?? p.innerText;
			const emojiMatch = text.match(/^([^\s]+)\s/);
			if (emojiMatch) {
				items.push({
					icono: emojiMatch[1],
					texto: text.substring(emojiMatch[1].length + 1).trim(),
				});
			}
		});
		return items;
	};
	const sabiasQueItems = parseSabiasQue(page.texts?.[7]?.content);
	const parqueYoyogiTitle = page.texts?.[8]?.content;
	const parqueYoyogiText = page.texts?.[9]?.content;
	const santuarioDescText = page.texts?.[10]?.content;
	const bodaStickerText = page.texts?.[11]?.content;
	const comoLlegarTitle = page.texts?.[12]?.content;
	const comoLlegarContent = page.texts?.[13]?.content;

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const estiloP = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor,
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor,
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
				{logoImage && !logoError && (
					<Box
						component="img"
						src={logoImage}
						alt=""
						onError={() => setLogoError(true)}
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* IMAGEN PORTADA */}
			<Box data-block-id="image-0" sx={{ position: "relative" }}>
				{meijiImage && !meijiError ? (
					<img
						src={meijiImage}
						alt="Meiji"
						onError={() => setMeijiError(true)}
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
				) : (
					<ImagenNoDisponibleKawaii
						width="100%"
						height={200}
						fontFamily={fontFamily}
					/>
				)}
				<Box
					component="h1"
					sx={{
						fontFamily: `${fontFamily} !important`,
						fontSize: 32,
						textAlign: "center",
						position: "absolute",
						bottom: 0,
						margin: "10px",
						left: "20px",
						color: "#fff",
						textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
					}}
				>
					{meijiTitle}
				</Box>
				{santuarioImage && !santuarioError && (
					<img
						src={santuarioImage}
						alt=""
						onError={() => setSantuarioError(true)}
						style={{ width: 90, position: "absolute", bottom: 50, left: 25 }}
					/>
				)}
			</Box>

			<Box data-block-id="text-1" sx={{ padding: "10px 12px", position: "relative" }}>
				<Box
					component="p"
					sx={{
						fontFamily: `${fontFamily} !important`,
						fontSize: 13,
						color: "#333",
						margin: 0,
						padding: 0,
					}}
					dangerouslySetInnerHTML={{ __html: introText }}
				/>
			</Box>

			{/* SECCIONES DESTACADAS */}
			<Box
				sx={{
					padding: "0 12px",
					display: "flex",
					flexDirection: "column",
					gap: "12px",
				}}
			>
				{/* Parque Yoyogi */}
				<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
					<Box sx={{ display: "flex", flexDirection: "column", width: "40%" }}>
						{meiji2Image && !meiji2Error ? (
							<img
								src={meiji2Image}
								alt="Meiji"
								onError={() => setMeiji2Error(true)}
								style={{
									width: "100%",
									height: 100,
									borderRadius: "12px",
									objectFit: "cover",
								}}
							/>
						) : (
							<ImagenNoDisponibleKawaii
								width="100%"
								height={100}
								fontFamily={fontFamily}
								compact
							/>
						)}
					</Box>
					{sabiasQueItems[0] && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? "52px" : "40px",
								left: isSmallScreen ? "32%" : "34%",
								zIndex: 30,
							}}
						>
							<SabiasQue
								icono={sabiasQueItems[0].icono}
								texto={sabiasQueItems[0].texto}
							/>
						</Box>
					)}
					{sabiasQueItems[1] && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? "12px" : 0,
								left: isSmallScreen ? "32%" : "34%",
								zIndex: 20,
							}}
						>
							<SabiasQue
								icono={sabiasQueItems[1].icono}
								texto={sabiasQueItems[1].texto}
							/>
						</Box>
					)}

					<Box sx={{ width: "60%", display: "flex", flexDirection: "column" }}>
						<Box>
							<Box component="h3" sx={estiloH3}>
								{parqueYoyogiTitle}
							</Box>
							<Box component="p" sx={estiloP}>
								{parqueYoyogiText}
							</Box>
						</Box>
					</Box>
				</Box>

				{/* Santuario Meiji */}
				<Box
					data-block-id="text-3"
					sx={{
						backgroundColor: "#ffe3cc",
						padding: "12px",
						borderRadius: "12px",
						display: "flex",
						gap: "16px",
						alignItems: "center",
						position: "relative",
						marginTop: isSmallScreen ? 0 : 0,
					}}
				>
					<Box sx={{ paddingLeft: "8px" }}>
						<Box component="h3" sx={estiloH3}>
							{meijiSubtitle}
						</Box>
						<Box component="p" sx={estiloP}>
							{santuarioDescText}
						</Box>
						<br />
					</Box>
					{santuario2Image && !santuario2Error ? (
						<img
							src={santuario2Image}
							alt="Santuario2"
							onError={() => setSantuario2Error(true)}
							style={{ width: 100 }}
						/>
					) : (
						<ImagenNoDisponibleKawaii
							width={100}
							height={80}
							fontFamily={fontFamily}
							compact
						/>
					)}
					{sabiasQueItems[2] && (
						<Box
							sx={{
								position: "absolute",
								top: isSmallScreen ? -10 : -12,
								right: isSmallScreen ? "40%" : "50%",
								zIndex: 10,
							}}
						>
							<SabiasQue
								icono={sabiasQueItems[2].icono}
								texto={sabiasQueItems[2].texto}
							/>
						</Box>
					)}
				</Box>

				{/* Cómo llegar */}
				<Box
					data-block-id="text-4"
					sx={{
						display: "flex",
						gap: "16px",
						justifyContent: "flex-end",
						width: "100%",
						position: "relative",
						marginTop: isSmallScreen ? "110px" : "0px",
					}}
				>
					{bodaImage && !bodaError && (
						<img
							src={bodaImage}
							alt=""
							onError={() => setBodaError(true)}
							style={{
								width: 110,
								position: "absolute",
								bottom: isSmallScreen ? "auto" : -25,
								left: isSmallScreen ? -25 : -10,
								top: isSmallScreen ? -100 : "auto",
							}}
						/>
					)}
					<Box
						sx={{
							position: "absolute",
							bottom: isSmallScreen ? "auto" : "50px",
							top: isSmallScreen ? -130 : "auto",
							left: isSmallScreen ? "65px" : "18px",
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 16px 0px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: `${fontFamily} !important`,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: isSmallScreen ? 220 : 160,
						}}
					>
						{bodaStickerText}
					</Box>

					<Box
						sx={{
							backgroundColor: "#ffe3cc",
							border: "3px solid #f28b35",
							borderRadius: "12px",
							padding: "8px 20px",
							fontSize: 12,
							fontFamily: `${fontFamily} !important`,
							color: "#333",
							width: isSmallScreen ? "100%" : "70%",
						}}
					>
						<Box
							component="h4"
							sx={{
								margin: 0,
								marginBottom: "8px",
								fontSize: 13,
								fontWeight: 700,
								display: "flex",
								alignItems: "center",
								gap: 6,
								color: "#f28b35!important",
								textTransform: "uppercase",
								fontFamily: `${fontFamily} !important`,
							}}
						>
							{comoLlegarTitle}
						</Box>
						<Box
							component="p"
							sx={{ paddingLeft: 0, margin: 0, lineHeight: 1.5 }}
							dangerouslySetInnerHTML={{ __html: comoLlegarContent }}
						/>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template54.thumbnail = "/images/template-1.png";

export default Template54;
